import { BREAKPOINTS } from "../config/breakpoints";
import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { Table, Tcol, Thead, Trow } from "../Generic/Table";
import Loader from "../Generic/Loader";
import EmptyState from "../Generic/EmptyState";
import { beautifyKey } from "../util/util";
import OrderService from "./Services/OrderService";
import { useLocation, useNavigate } from "react-router-dom";
import { bindActionCreators } from "redux";
import * as Actions from "./Actions/index";
import { connect } from "react-redux";
import { errorNotification } from "../Generic/Notifications";
import useWindowSize from "../util/hooks/useWindowSize";
import { LIST_DISPLAY_DATE_FORMAT } from "../util/date.util";
import Timestamp from "../Generic/Timestamp";
import { zIndex } from "../config/zIndex";

const OrderStatuses = {
	CREATED: "CREATED",
	IN_QUEUE: "IN_QUEUE",
	IN_PROGRESS: "IN_PROGRESS",
	COMPLETED: "COMPLETED",
	CANCELLED: "CANCELLED",
	ABORTED: "ABORTED",
	ARCHIVED: "ARCHIVED",
};

const PaymentStatuses = {
	PAYMENT_PENDING: "PAYMENT_PENDING",
	PAYMENT_SUCCESSFUL: "PAYMENT_SUCCESSFUL",
	PAYMENT_FAILED: "PAYMENT_FAILED",
	REFUND_INITIATED: "REFUND_INITIATED",
	REFUND_PROCESSED: "REFUND_PROCESSED",
	REFUND_FAILED: "REFUND_FAILED",
};

const orderStatusMap = {
	[OrderStatuses.CREATED]: {
		statusMessage: "CREATED",
		status: 1,
		backColor: "#000000",
		textColor: "#FFFFFF",
	},
	[OrderStatuses.IN_QUEUE]: {
		statusMessage: "IN QUEUE",
		status: 2,
		backColor: "#f6ea76",
		textColor: "#776200",
	},
	[OrderStatuses.IN_PROGRESS]: {
		statusMessage: "IN PROGRESS",
		status: 3,
		backColor: "#54BF29",
		textColor: "#ffffff",
	},
	[OrderStatuses.COMPLETED]: {
		statusMessage: "COMPLETED",
		status: 5,
		backColor: "#008000",
		textColor: "#ffffff",
	},
	[OrderStatuses.CANCELLED]: {
		statusMessage: "CANCELLED",
		status: 6,
		backColor: "#d64a4a",
		textColor: "#ffffff",
	},
	[OrderStatuses.ABORTED]: {
		statusMessage: "ABORTED",
		status: 7,
		backColor: "#d64a4a",
		textColor: "#ffffff",
	},
	[OrderStatuses.ARCHIVED]: {
		statusMessage: "ARCHIVED",
		status: 8,
		backColor: "#d64a4a",
		textColor: "#ffffff",
	},
};

const paymentStatusMap = {
	[PaymentStatuses.PAYMENT_PENDING]: {
		status: "PAYMENT_PENDING",
		backColor: "#f6ea76",
		textColor: "#776200",
	},
	[PaymentStatuses.PAYMENT_SUCCESSFUL]: {
		status: "PAYMENT_SUCCESSFUL",
		backColor: "#008000",
		textColor: "#ffffff",
	},
	[PaymentStatuses.PAYMENT_FAILED]: {
		status: "PAYMENT_FAILED",
		backColor: "#d64a4a",
		textColor: "#ffffff",
	},
	[PaymentStatuses.REFUND_INITIATED]: {
		status: "REFUND_INITIATED",
		backColor: "#f6ea76",
		textColor: "#776200",
	},
	[PaymentStatuses.REFUND_PROCESSED]: {
		status: "REFUND_PROCESSED",
		backColor: "#008000",
		textColor: "#ffffff",
	},
	[PaymentStatuses.REFUND_FAILED]: {
		status: "REFUND_FAILED",
		backColor: "#d64a4a",
		textColor: "#ffffff",
	},
};

const Page = styled.div`
	height: max-content;
	width: 100%;
	display: flex;
	flex-direction: column;
	align-items: center;
	padding: 2rem;
	scroll-behavior: smooth;
	@media (max-width: ${BREAKPOINTS.tablet}px) {
		padding: 1rem;
	}
`;

const Header = styled.div`
	display: flex;
	font-size: 2rem;
	margin: 0 0 1rem 0.5rem;
	@media (max-width: ${BREAKPOINTS.tablet}px) {
		font-size: 1.5rem;
	}
`;

const Div = styled.div`
	position: ${(props) => props.position};
	background: ${(props) => props.background};
	color: ${(props) => props.color};
	align-self: ${(props) => props.alignSelf};
	display: flex;
	gap: ${(props) => props.gap};
	height: ${(props) => props.height};
	width: ${(props) => props.width};
	flex-direction: ${(props) => props.flexD};
	flex-wrap: ${(props) => props.flexWrap};
	justify-content: ${(props) => props.jContent};
	align-items: ${(props) => props.alignItem};
	padding: ${(props) => props.padding};
	margin: ${(props) => props.margin};
	text-align: ${(props) => props.txtAlign};
	font-size: ${(props) => props.fontSize};
	border-radius: ${(props) => props.borderRadius};
	z-index: ${(props) => props.zIndex};
`;

const OrdersList = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
	gap: 0.5rem;
`;

const OrderCard = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	align-items: center;
	gap: 0.75rem 1rem;
	background: #fff;
	border-radius: 10px;
	padding: 1rem;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
	cursor: pointer;
	transition: box-shadow 0.15s ease;
	&:hover {
		box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
	}
`;

const Cell = styled.div`
	display: flex;
	flex-direction: column;
	min-width: 0;
	${(props) => props.fullWidth && `grid-column: 1 / -1;`}
`;

const CellLabel = styled.span`
	display: block;
	font-size: 0.7rem;
	text-transform: uppercase;
	letter-spacing: 0.06em;
	color: #6b7280;
	margin-bottom: 0.2rem;
`;

const CellValue = styled.span`
	font-size: 0.9rem;
	font-weight: 500;
	color: #111827;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	${(props) =>
		props.noEllipsis &&
		`
		white-space: normal;
		overflow: visible;
		text-overflow: clip;
	`}
`;

const StatusBadge = styled.div`
	align-self: flex-start;
	display: flex;
	align-items: center;
	justify-content: center;
	background: ${(props) => props.background};
	color: ${(props) => props.color};
	font-size: 0.75rem;
	font-weight: 600;
	letter-spacing: 0.03em;
	padding: 0.35rem 0.8rem;
	border-radius: 20px;
	white-space: nowrap;
`;

const getOrderStatusMeta = (status) =>
	orderStatusMap[status] || { statusMessage: beautifyKey(status), backColor: "#e5e7eb", textColor: "#374151" };

const getPaymentStatusMeta = (status) =>
	paymentStatusMap[status] || { backColor: "#e5e7eb", textColor: "#374151" };

const CartItem = styled.div`
	border: 1px solid rgba(0, 0, 0, 0.2);
	box-sizing: border-box;
	border-radius: 18px;
	padding-left: 20px;
	padding-right: 20px;
	padding-top: 16px;
	padding-bottom: 16px;
	height: max-content;
	width: 100%;
	display: flex;
	align-items: center;
	justify-content: space-between;
	flex-direction: row;
	margin-top: 18px;
`;

const Overlay = styled.div`
	position: fixed;
	display: flex;
	justify-content: center;
	align-items: center;
	background: rgba(0, 0, 0, 0.3);
	left: 0;
	top: 0;
	right: 0;
	bottom: 0;
	z-index: ${zIndex.modalBackdrop};
`;

const Modal = styled.div`
	display: flex;
	flex-direction: column;
	background: #ffffff;
	height: max-content;
	width: 60%;
	border-radius: 10px;
	overflow-x: hidden;
	z-index: ${zIndex.modal};
	@media (max-width: ${BREAKPOINTS.tablet}px) {
		width: 95%;
	}
	@media (max-width: 600px) {
		width: 100%;
		height: 100%;
		overflow-y: scroll;
		border-radius: 0px;
	}
`;

const TabContain = styled.div`
	background: #fff;
	display: flex;
	width: max-content;
	align-items: center;
	padding: 0 0;
	margin: 1rem 1rem 1rem 1rem;
	border-radius: 10px;
	@media (max-width: ${BREAKPOINTS.tablet}px) {
		width: 100%;
		overflow-x: auto;
		padding-bottom: 0.5rem;
	}
`;

const Tab = styled.div`
	margin: 0;
	padding: 1rem;
	width: max-content;
	display: flex;
	z-index: ${zIndex.tab};
	background: transparent;
	transition: 0.3s all linear;
	flex-shrink: 0;
`;

const Slider = styled.div`
	position: absolute;
	background: darkgrey;
	border-radius: 10px;
	transition: 0.2s all linear;
`;

const Popper = styled.div`
	position: absolute;
	top: 0;
	left: 0;
	z-index: ${zIndex.popover};
	display: flex;
	flex-direction: row;
`;

const yesterday = () => {
	let d = new Date();
	d.setDate(d.getDate() - 1);
	return d;
};

const FilterHead = styled.div`
	padding: 0.5rem 1rem;
	color: ${(props) => (props.active ? "#FFF" : "#000")};
	background: ${(props) => (props.active ? "rgba(45, 35, 66, 0.8)" : "#FFF")};
	border-radius: 10px;
`;

const DATA_MODES = {
	APPEND: "APPEND",
	REPLACE: "REPLACE",
};

function scrollToRequiredItem(id) {
	const element = document.getElementById(id);
	if (!element) return;
	element.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
}

const RecentOrders = (props) => {
	const { machineID, setOrders, orders, lastClickedElement, clearState, setLastClickedElement } = props;
	const { state } = useLocation();
	const preserveState = state?.preserveState && orders.length ? true : false;
	const [filters, setFilters] = useState({
		page: 1,
	});
	const [mode, setMode] = useState(preserveState ? DATA_MODES.APPEND : DATA_MODES.REPLACE);
	const [totalCount, setTotalCount] = useState(0);
	const [loading, setLoading] = useState(!preserveState);
	const [error, setError] = useState(false);
	const limitReached = useRef(false);
	const [lastElement, setLastElement] = useState();
	const navigate = useNavigate();
	const skipInitialCall = useRef(preserveState);
	const [width] = useWindowSize();
	const isMobile = width < BREAKPOINTS.tablet;

	const observer = useRef(
		new IntersectionObserver(
			(entries) => {
				if (entries[0].isIntersecting && !limitReached.current) {
					//Getting the earlier state and incrementing it
					setMode(DATA_MODES.APPEND);
					setFilters((prev) => ({ ...prev, page: prev.page + 1 }));
				}
			},
			{
				//Threshold set to 1 for the page to increment only when the element is 100% visible
				//Threshold = % of element to be visible / 100
				threshold: 1,
			}
		)
	);

	useEffect(() => {
		//Update the element on which the call is made and set the observer on it once the list re-renders
		const currentElement = lastElement;
		const currentObserver = observer.current;

		if (currentElement) {
			currentObserver.observe(currentElement);
		}

		//Remove the observer once user exits the page
		return () => {
			if (currentElement) {
				currentObserver.unobserve(currentElement);
			}
		};
	}, [lastElement]);

	useEffect(() => {
		if (preserveState && lastClickedElement) {
			scrollToRequiredItem(lastClickedElement.id);
		} else {
			clearState();
		}
	}, [preserveState]);

	useEffect(() => {
		if (skipInitialCall.current) {
			skipInitialCall.current = false;
			return;
		}
		//Fires everytime page changes
		fetchOrders(filters);
	}, [filters]);

	async function fetchOrders(filters) {
		if (limitReached.current) return;

		setLoading(true);
		setError(false);
		if (mode === DATA_MODES.REPLACE) {
			setOrders([]);
		}
		const response = await OrderService.getRecentOrders(machineID, filters);
		const { data, error } = response;
		setLoading(false);
		if (data) {
			const { orders: newOrders, totalOrdersCount } = data;

			if (mode === DATA_MODES.APPEND) {
				const totalOrders = [...orders, ...newOrders];
				if (totalOrders.length >= totalOrdersCount) {
					limitReached.current = true;
				}
				setOrders(totalOrders);
			} else {
				if (newOrders.length >= totalOrdersCount) {
					limitReached.current = true;
				}
				setOrders(newOrders);
			}
		}
		if (error) {
			errorNotification("Failed to Get Orders");
			setError(true);
		}
	}

	function renderOrders(orders) {
		if (!orders?.length) return;
		if (isMobile) {
			return (
				<OrdersList>
					{orders.map((item, index, array) => {
						const isLastELement = array.length - 1 === index;
						const orderStatus = getOrderStatusMeta(item.status);
						const paymentStatus = getPaymentStatusMeta(item.paymentStatus);

						return (
							<OrderCard
								ref={(node) => {
									if (isLastELement) setLastElement(node);
								}}
								id={item._id}
								key={item._id}
								onClick={() => {
									setLastClickedElement({ id: item._id });
									navigate(`/${machineID}/order/details/${item._id}`);
								}}>
								<Cell>
									<CellLabel>Order Id</CellLabel>
									<CellValue>{item._id}</CellValue>
								</Cell>
								<Cell>
									<CellLabel>Item Count</CellLabel>
									<CellValue>{item.orderItemCount}</CellValue>
								</Cell>
								<Cell fullWidth>
									<CellLabel>Date</CellLabel>
									<CellValue noEllipsis><Timestamp value={item.createdAt} format={LIST_DISPLAY_DATE_FORMAT} /></CellValue>
								</Cell>
								<Cell>
									<CellLabel>Order Item Status</CellLabel>
									<StatusBadge background={orderStatus.backColor} color={orderStatus.textColor}>
										{orderStatus.statusMessage}
									</StatusBadge>
								</Cell>
								<Cell>
									<CellLabel>Payment Status</CellLabel>
									<StatusBadge background={paymentStatus.backColor} color={paymentStatus.textColor}>
										{beautifyKey(item.paymentStatus)}
									</StatusBadge>
								</Cell>
							</OrderCard>
						);
					})}
				</OrdersList>
			);
		}
		return (
			<Table>
				<Trow head>
					<Thead>Order Id</Thead>
					<Thead>Order Item Count</Thead>
					<Thead>Date</Thead>
					<Thead>Order Status</Thead>
					<Thead>Payment Status</Thead>
				</Trow>
				{orders.map((item, index, array) => {
					const isLastELement = array.length - 1 === index;

					return (
						<Trow
							innerRef={(node) => {
								if (isLastELement) setLastElement(node);
							}}
							id={item._id}
							key={item._id}
							onClick={() => {
								setLastClickedElement({ id: item._id });
								navigate(`/${machineID}/order/details/${item._id}`);
							}}>
							<Tcol borderRadius="10px 0 0 10px">{item._id}</Tcol>
							<Tcol>{item.orderItemCount}</Tcol>
							<Tcol><Timestamp value={item.createdAt} /></Tcol>
							<Tcol>{item.status}</Tcol>
							<Tcol borderRadius="0 10px 10px 0">{item.paymentStatus}</Tcol>
						</Trow>
					);
				})}
			</Table>
		);
	}

	return (
		<Page>
			<Div flexWrap="wrap" margin="0 0 0 0" alignItem="center" jContent="space-between" width="100%">
				<Header>Recent Orders</Header>
			</Div>
			{!error && renderOrders(orders)}
			{loading ? <Loader /> : null}
			{error && !orders.length ? <EmptyState messsage="Something Went Wrong." retry={() => fetchOrders(filters)} /> : ""}
			{!loading && !error && !orders.length ? <EmptyState messsage="No Orders Found." retry={fetchOrders(filters)} /> : ""}
		</Page>
	);
};

const mapStatetoProps = (state) => {
	const { orders, landing } = state;
	return {
		machineID: landing.machineID,
		kitchenId: landing.kitchenId,
		...orders,
	};
};

const mapDispatchToProps = (dispatch) => ({
	...bindActionCreators(
		{
			...Actions,
		},
		dispatch
	),
});

export default connect(mapStatetoProps, mapDispatchToProps)(RecentOrders);
